/**
 * Phase 4.4 — Role + permission-based UI gating.
 *
 * Read-only helper hook that maps the authenticated user's role & scope.permissions
 * to a small set of UI booleans.
 *
 * The backend (Phase 4.1, 4.2, 4.3) is the source of truth and already enforces
 * these rules. This hook is purely cosmetic: it hides buttons completely rather
 * than showing disabled controls.
 *
 * Behavior matrix (unchanged for super_admin and legacy creator):
 *
 *                  super_admin   creator   admin   editor               viewer
 *   canWrite       true          true      true    true                 false
 *   canPublish     true          true      true    can_publish          false
 *   canExport      true          true      can_export  can_export       can_export
 *   canDelete      true          true      true    true                 false
 *   canShare       true          true      true    true                 false
 *   canReset       true          true      true    true                 false
 */

import { useAuth } from '../contexts/AuthContext';

const ROLE_SUPER_ADMIN = 'super_admin';
const ROLE_ADMIN = 'admin';
const ROLE_EDITOR = 'editor';
const ROLE_VIEWER = 'viewer';
const ROLE_CREATOR = 'creator';

export function usePermissions() {
  const { user } = useAuth();
  const role = user?.role || ROLE_CREATOR;
  const perms = (user?.scope?.permissions) || {};

  const isSuperAdmin = role === ROLE_SUPER_ADMIN;
  const isLegacyCreator = role === ROLE_CREATOR;
  const isAdmin = role === ROLE_ADMIN;
  const isEditor = role === ROLE_EDITOR;
  const isViewer = role === ROLE_VIEWER;

  // Legacy creator + super_admin: never gated by perms (byte-identical to today).
  const unrestricted = isSuperAdmin || isLegacyCreator;

  const canWrite = unrestricted || isAdmin || isEditor;          // viewer → false
  const canDelete = canWrite;
  const canShare = canWrite;
  const canReset = canWrite;

  const canPublish =
    unrestricted ||
    isAdmin ||
    (isEditor && perms.can_publish === true);

  const canExport =
    unrestricted ||
    ((isAdmin || isEditor || isViewer) && perms.can_export === true);

  return {
    role,
    isSuperAdmin,
    isLegacyCreator,
    isAdmin,
    isEditor,
    isViewer,
    canWrite,
    canPublish,
    canExport,
    canDelete,
    canShare,
    canReset,
  };
}

export default usePermissions;
